import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'

/**
 * "Generations of Excellence" e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "ebook" ve DAIMI gibi yerleşik terimler de öyle.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: aile şirketini okuyup indirme kararını veren
 * kişi Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ve görsel çözünürlüğüyle ilgili açık sorular İngilizce sayfada duruyor
 * ve orada bir kez duruyor: `check` iki dilde tekrarlanınca aynı karar iki not
 * olarak sayılıyor.
 */

/** İngilizce sayfadaki PDF ile aynı dosya — bkz. `content/pages/ebook-generations-of-excellence.ts`. */
const PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '0ca92deb-b73d-405f-b435-b86b676332de-Generations%20of%20Excellence.pdf'

export const ebookGenerationsOfExcellence: WrittenPage = {
  navLabel: 'Generations of Excellence',
  title: 'Generations of Excellence',
  description:
    'Aile şirketinizi dönüştürmek ve sürdürmek için beş adım ve mirası kuşaklar boyu büyüten stratejiler. Digitopia Impact Labs’in e-kitabını indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Aile şirketinizi dönüştürmek ve sürdürmek için 5 adım',
      headline: 'Generations of Excellence',
      body: [
        'Aile şirketlerinin, korunmayı ve büyütülmeyi hak eden kendine özgü bir mirası ve birikimi '
          + 'var. Kapsamlı e-kitabımız, aile şirketinizin gelecek kuşaklar boyunca güçlü kalması '
          + 'için gereken temel stratejileri bir araya getiriyor.',
      ],
      ctas: [{ label: 'İndirin', href: PDF }],
      photo: {
        src: '/photo/generations-of-excellence-ebook-artwork.webp',
        alt:
          'E-kitabın kapak fotoğrafı: geniş bir merdivenin tepesinde diz çökmüş bir ebeveyn, '
          + 'aydınlık gökyüzüne karşı çocuğuna bir şey gösteriyor.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Neler öğreneceksiniz?',
      points: [
        'Değişimi kucaklayın: Pazar dinamiklerini nasıl okuyacağınızı ve önde kalmak için nasıl '
          + 'kullanacağınızı keşfedin.',
        'Sürdürülebilirliği kurun: İtibarınızı güçlendiren ve uzun vadeli kalıcılığı güvenceye '
          + 'alan çevre dostu uygulamaları işin içine katın.',
        'İnovasyonu besleyin: Yaratıcılığı, birlikte çalışmayı ve sürekli iyileştirmeyi '
          + 'cesaretlendiren bir kültür kurun.',
        'Ölçün ve izleyin: İlerlemenizi takip etmek ve dönüşümü başarıya taşımak için stratejik '
          + 'araçlardan yararlanın.',
        'Geleceğin liderlerini hazırlayın: Şirketinizin mirasını güvenceye almak için yeni kuşağa '
          + 'nasıl mentorluk edeceğinizi ve onu nasıl geliştireceğinizi öğrenin.',
      ],
      ctas: [{ label: 'Raporu indirin', href: PDF }],
      photo: {
        src: '/photo/generations-of-excellence-ebook-covers.webp',
        alt:
          'Generations of Excellence’ın iki kopyası, biri diğerinin arkasında, beyaz zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neden bu e-kitap?',
      body: [
        'Bugünün hızlı ve sürekli değişen iş dünyasında rekabetçi kalmak, gelenekten fazlasını '
          + 'gerektiriyor. Bu e-kitap, aile şirketinizi dönüştürmeniz ve sürdürmeniz için '
          + 'uygulanabilir içgörüler ve sahadan vakalar sunuyor. İster inovasyon, ister '
          + 'sürdürülebilirlik, ister yeni kuşağı liderliğe hazırlamak peşinde olun; bu rehber '
          + 'kuşaklar boyu sürecek bir başarıya giden yol haritanız.',
      ],
      ctas: [{ label: 'Raporu indirin', href: PDF }],
      photo: {
        src: '/photo/generations-of-excellence-ebook-stack.webp',
        alt:
          'Generations of Excellence’ın basılı kopyalarından oluşan bir yığın, yukarıdan görünüm.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'DAIMI ile bir adım önde olun',
      sub:
        'Şirketinizin önündeki zorluklara ve fırsatlara hazır olduğundan emin olun. Digital & AI '
        + 'Maturity Index (DAIMI), şirketinizin geleceğe hazırlığına dair değerli içgörüler '
        + 'sunar; güçlü olduğunuz ve iyileştirmeniz gereken alanları görmenizi sağlar.',
      cta: { label: 'Daha fazlası', href: LINKS.daimi },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Kaçırmayın! Ücretsiz e-kitabınızı şimdi indirin',
      sub:
        'Mirasını başarıyla dönüştürüp sürdüren yüzlerce aile şirketine katılın. Ücretsiz '
        + 'kopyanızı bugün indirin.',
      cta: { label: 'E-kitabı indirin', href: PDF },
    },
    {
      kind: 'prose',
      heading: 'Hakkımızda',
      paragraphs: [
        'Aile şirketlerinin güçlenmesi için çalışıyoruz. Onlarca yıllık deneyimimiz ve geniş '
          + 'birikimimizle, bugünün iş dünyasının karmaşıklığında yol almanız için gereken '
          + 'rehberliği ve desteği veriyoruz. Aile şirketiniz için kalıcı bir miras kurmanıza '
          + 'yardımcı olalım.',
        'Birlikte, aile şirketlerinin güçlü kalmaya devam ettiği ve dünyada olumlu bir iz '
          + 'bıraktığı bir gelecek kurabiliriz. Daha fazlası için bugün bize ulaşın.',
      ],
      cta: { label: 'Bize Ulaşın', href: '#contact' },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'Dijital olgunluk ölçüm süreci bizim için akıcı bir deneyim oldu. Digitopia delivery '
            + 'ekibi, ilk adımı atmadan önce Arzum’un iç dinamiklerini anlamaya zaman ayırdı. '
            + 'Şirketimizin kendine özgü iş modelini içselleştirip süreci ona göre yürüttüler; bu '
            + 'farkı ilk oturumun başından son oturuma kadar hissettik. Koç yaklaşımıyla, doğru '
            + 'kişilere doğru soruları sorarak kendimizi çok kısa sürede değerlendirmemizi '
            + 'sağladılar. Altı ayrı boyuta odaklanarak her dikey için ayrı çalışmalar yürüttüler.',
          source: 'Arif Emre Unal, Executive Board Member, CIO & COO',
          company: 'Arzum',
          logo: { name: 'Arzum', src: '/logo/arzumcard.png' },
          portrait: { src: '/photo/portrait-arif-emre-unal.png', alt: 'Arif Emre Unal' },
        },
      ],
    },
  ],
}
